import { ref, type Ref } from 'vue'

export interface Notification {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration: number;
}

class NotificationService {
  private notifications: Ref<Notification[]> = ref([])
  private nextId = 0

  // Getter for notifications (for use in Vue components)
  getNotifications(): Ref<Notification[]> {
    return this.notifications
  }

  // Add a notification
  add(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', duration: number = 10000) {
    const id = ++this.nextId
    this.notifications.value.push({
      id,
      message,
      type,
      duration
    })

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.remove(id)
      }, duration)
    }
  }

  // Remove a notification by id
  remove(id: number) {
    const index = this.notifications.value.findIndex(n => n.id === id)
    if (index !== -1) {
      this.notifications.value.splice(index, 1)
    }
  }

  // Clear all notifications
  clear() {
    this.notifications.value = []
  }

  // Convenience methods
  success(message: string, duration?: number) {
    this.add(message, 'success', duration)
  }

  error(message: string, duration?: number) {
    this.add(message, 'error', duration)
  }

  info(message: string, duration?: number) {
    this.add(message, 'info', duration)
  }

  warning(message: string, duration?: number) {
    this.add(message, 'warning', duration)
  }
}

// Export a singleton instance
export const notificationService = new NotificationService()
